import cn from "classnames";
import React, { useMemo } from "react";
import { capitalize } from "lodash";
import { useIntl } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilePdf,
  faFile,
  faDownload,
  faFileWord,
  faFileExcel,
} from "@fortawesome/free-solid-svg-icons";

import { WebMessage } from "../../../cores/conversations/interfaces/api/get-messages";
import moment from "moment";
import { Message } from "../../../cores/socialChat/interfaces/conversations.interface";
import Avatar from "../Avatar";
import { pickBestAvatar } from "../../../cores/socialChat/utils/avatar-url";
import { getFirstLetters } from "../../utils/stringUtils";
import { daysRemainingFromToday } from "../../utils/dateUtils";
import "./ChatBubbles.scss";

interface Props {
  message: WebMessage | Message;
  itsMe: boolean;
  /** Fallback when message.from.avatarUrl is missing (incoming contact). */
  contactAvatarUrl?: string;
  /** Fallback when message.from.avatarUrl is missing (outgoing / business account). */
  senderAvatarUrl?: string;
}

/** Debe coincidir con lo que realmente pinta el bubble (URLs cortas = sin enlace en document). */
function messageHasVisibleContent(message: WebMessage | Message): boolean {
  const typeLower = (message.type || "").toLowerCase();
  const knownMedia = ["text", "audio", "video", "document", "image", "sticker"];
  const m = String(message.message ?? "").trim();

  if (!message.type) {
    return m.length > 0;
  }
  if (typeLower === "text") return m.length > 0;
  if (typeLower === "audio" || typeLower === "video") return m.length > 0;
  if (typeLower === "document") return m.length > 1;
  if (typeLower === "image") return m.length > 0;
  if (typeLower === "sticker") return m.length > 0;
  if (message.type && !knownMedia.includes(typeLower)) {
    return m.length > 0;
  }
  return false;
}

export default function MessageRow(props: Props) {
  const intl = useIntl();
  const { message, itsMe, contactAvatarUrl, senderAvatarUrl } = props;
  function getFileExtension(url: string): string {
    const parts = url.split(".");
    if (parts.length > 1) {
      return parts.pop()!.toLowerCase();
    }
    return "";
  }

  const dateLabel = useMemo(() => {
    const full = message.timestamp?.full;
    if (!full) return "";
    const days = daysRemainingFromToday(full);

    if (days > -10) {
      return capitalize(moment(full).calendar());
    }
    return capitalize(moment(full).format("llll"));
  }, [message.timestamp?.full]);

  const typeLower = (message.type || "").toLowerCase();
  const knownMedia = ["text", "audio", "video", "document", "image", "sticker"];
  const isSticker = typeLower === "sticker";

  const bubbleBody = message.type ? (
    <>
      {typeLower === "text" && (
        <div
          className={cn("m-0 p-0", { "text-white": itsMe })}
          dangerouslySetInnerHTML={{
            __html: String(message.message || "").replaceAll("\n", "<br />"),
          }}
        />
      )}
      {message.type === "audio" && (
        <div className="audio-player">
          <audio controls>
            <source src={message.message} type="audio/mpeg" />
            Tu navegador no soporta la reproducción de audio.
          </audio>
        </div>
      )}
      {message.type === "video" && (
        <div
          className="overflow-hidden rounded"
          style={{
            maxWidth: "100%",
          }}
        >
          <video controls className="w-100" style={{ maxHeight: "280px" }}>
            <source src={message.message} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      )}
      {message.type === "document" && (
        <div className="audio-player">
          {message.message && message.message.length > 1 && (
            <a
              href={message.message}
              className={cn({ "chat-bubble__link": itsMe })}
              style={!itsMe ? { color: "#2563eb" } : undefined}
            >
              {getFileExtension(message.message) === "docx" ? (
                <>
                  <FontAwesomeIcon icon={faFileWord} className="mr-1" />
                  Descargar Documento de texto
                </>
              ) : getFileExtension(message.message) === "pdf" ? (
                <>
                  <FontAwesomeIcon icon={faFilePdf} className="mr-1" />
                  Descargar Archivo PDF
                </>
              ) : getFileExtension(message.message) === "xlsx" ? (
                <>
                  <FontAwesomeIcon icon={faFileExcel} className="mr-1" />
                  Descargar Hoja de Calculo
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faFile} className="mr-1" />
                  Descargar Archivo
                </>
              )}
            </a>
          )}
        </div>
      )}
      {typeLower === "image" && (
        <div className="chat-image">
          <img
            src={message.message}
            alt=""
            className="chat-image__img"
            loading="lazy"
          />
          <a
            href={message.message}
            className={cn(
              "chat-image__download",
              itsMe ? "chat-image__download--sent" : "chat-image__download--received"
            )}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Download image"
          >
            <FontAwesomeIcon icon={faDownload} />
          </a>
        </div>
      )}
      {typeLower === "sticker" && (
        <img
          src={message.message}
          alt=""
          className="chat-sticker"
          loading="lazy"
        />
      )}
      {message.type && !knownMedia.includes(typeLower) && (
        <div
          className={cn("m-0 p-0", { "text-white": itsMe })}
          dangerouslySetInnerHTML={{
            __html: String(message.message || "").replaceAll("\n", "<br />"),
          }}
        />
      )}
    </>
  ) : (
    <>{message.message}</>
  );

  const showPlaceholder = !messageHasVisibleContent(message);

  const avatarImage = useMemo(() => {
    if (itsMe) {
      return pickBestAvatar(message.from.avatarUrl, senderAvatarUrl);
    }
    return pickBestAvatar(contactAvatarUrl, message.from.avatarUrl);
  }, [message.from.avatarUrl, itsMe, contactAvatarUrl, senderAvatarUrl]);

  return (
    <div
      key={message.id}
      className={cn("w-100 d-flex", { "justify-content-end": itsMe })}
    >
      <div
        className={cn(
          "message-row-cluster mb-2 d-flex flex-column",
          itsMe
            ? "message-row--outgoing align-items-end"
            : "message-row--incoming align-items-start"
        )}
      >
        <div className="message-row-cluster__row">
          {!itsMe && (
            <Avatar
              className="shadow-sm"
              width="36px"
              height="36px"
              label={getFirstLetters(message.from.name)}
              image={avatarImage}
            />
          )}
          <div className="d-flex flex-column flex-grow-1 min-w-0">
            {isSticker ? (
              <>
                {showPlaceholder ? (
                  <div
                    className="small text-muted px-1"
                    style={{ opacity: 0.85 }}
                  >
                    {intl.formatMessage({
                      id: "app.chat.message.bodyUnavailable",
                    })}
                  </div>
                ) : (
                  bubbleBody
                )}
                <div className="chat-bubble__time px-1">{dateLabel}</div>
              </>
            ) : (
              <>
                <div
                  className={cn(
                    "chat-bubble fs-15",
                    itsMe ? "chat-bubble--sent" : "chat-bubble--received"
                  )}
                >
                  {showPlaceholder ? (
                    <div
                      className={cn("m-0 p-0 small", { "text-white": itsMe })}
                      style={itsMe ? undefined : { opacity: 0.85 }}
                    >
                      {intl.formatMessage({
                        id: "app.chat.message.bodyUnavailable",
                      })}
                    </div>
                  ) : (
                    bubbleBody
                  )}
                </div>
                <div className="chat-bubble__time px-1">{dateLabel}</div>
              </>
            )}
          </div>
          {itsMe && (
            <Avatar
              className="shadow-sm"
              width="36px"
              height="36px"
              label={getFirstLetters(message.from.name)}
              image={avatarImage}
            />
          )}
        </div>
      </div>
    </div>
  );
}
